import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ShoppingCart, Trash2, Truck } from 'lucide-react';
import axios from 'axios';
import Seo from '@/components/Seo';
import { useCart } from '@/context/CartContext';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { customerApi } from '@/lib/customerApi';
import { formatApiError } from '@/lib/api';
import { saveFreightMemory, clearFreightMemory, cartSignature } from '@/lib/freightMemory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (value) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(value || 0));

export default function Cart() {
  const cart = useCart();
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [priced, setPriced] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [postcode, setPostcode] = useState('');
  const [suburbs, setSuburbs] = useState([]);
  const [suburb, setSuburb] = useState('');
  const [freight, setFreight] = useState(null);
  const [freightMode, setFreightMode] = useState('delivery');
  const [tailgate, setTailgate] = useState(false);
  const [freightBusy, setFreightBusy] = useState(false);
  const [freightError, setFreightError] = useState('');

  const lookupSuburbs = async (value) => {
    setPostcode(value);
    setFreight(null);
    setSuburb('');
    setSuburbs([]);
    setFreightError('');
    if (!/^\d{4}$/.test(value)) return;
    try {
      const { data } = await axios.get(`${API}/account/cart/localities`, { params: { postcode: value } });
      setSuburbs(data.localities || []);
      if ((data.localities || []).length === 1) setSuburb(data.localities[0].suburb);
      if (!(data.localities || []).length) setFreightError('We have no carrier listing for that postcode, so our team will quote the freight.');
    } catch (failure) { setFreightError(formatApiError(failure, 'Could not look up that postcode')); }
  };

  const estimateFreight = async () => {
    setFreightBusy(true); setFreightError(''); setFreight(null);
    try {
      const { data } = await axios.post(`${API}/account/cart/freight`, {
        lines: cart.lines.map(({ kind, id, quantity }) => ({ kind, id, quantity })),
        postcode, suburb, residential: false, tailgate, mode: freightMode,
      });
      setFreight(data);
      // Remembered so the cart drawer can show the same estimate without asking again.
      if (data.freight_estimate_inc_gst != null) {
        saveFreightMemory({ postcode, suburb, mode: data.mode, amount_inc_gst: data.freight_estimate_inc_gst, signature: cartSignature(cart.lines) });
      } else clearFreightMemory();
      if (data.reason) setFreightError(data.reason);
    } catch (failure) { setFreightError(formatApiError(failure, 'Could not estimate freight')); }
    finally { setFreightBusy(false); }
  };

  const price = useCallback(async () => {
    if (!cart.lines.length) { setPriced(null); return; }
    try {
      const { data } = await axios.post(`${API}/account/cart/price`, { lines: cart.lines.map(({ kind, id, quantity }) => ({ kind, id, quantity })) });
      setPriced(data);
      setError('');
    } catch (failure) { setError(formatApiError(failure, 'Could not price this cart')); }
  }, [cart.lines]);
  useEffect(() => { price(); }, [price]);

  const checkout = async () => {
    if (!customer) { navigate('/account/login?returnTo=/cart'); return; }
    setBusy(true); setError('');
    try {
      const { data } = await customerApi.post('/account/cart/checkout', {
        lines: cart.lines.map(({ kind, id, quantity }) => ({ kind, id, quantity })),
        fulfilment: freight && postcode && suburb
          ? { mode: freight.mode, postcode, suburb, warehouse_id: freight.dispatch?.warehouse_id || null, freight_estimate_inc_gst: freight.freight_estimate_inc_gst }
          : null,
      });
      window.location.href = data.checkout_url;
    } catch (failure) { setError(formatApiError(failure, 'Checkout could not be opened. No payment was taken.')); setBusy(false); }
  };

  return (
    <>
      <Seo title="Your cart — EI Battery Solutions" description="Trade cart: equipment priced ex GST, GST added at checkout, freight quoted separately." />
      <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-16" data-testid="cart-page">
        <p className="ei-eyebrow text-ei-golddark">Trade-only · Supply-only</p>
        <h1 className="mt-2 font-display text-3xl font-bold text-ei-navy sm:text-4xl">Your cart</h1>

        {!cart.lines.length ? (
          <div className="mt-8 rounded-md border border-ei-mist bg-white p-8" data-testid="cart-empty">
            <p className="text-sm text-ei-graphite">Your cart is empty.</p>
            <Link to="/products" className="mt-5 inline-flex min-h-[44px] items-center rounded-sm bg-ei-navy px-5 py-3 text-sm font-bold text-white" data-testid="cart-browse">Browse products</Link>
          </div>
        ) : (
          <div className="mt-8 space-y-8">
            <div className="space-y-4">
              {cart.lines.map((line) => {
                const row = priced?.lines?.find((item) => item.id === line.id && item.kind === line.kind);
                return (
                  <div key={`${line.kind}-${line.id}`} className="flex flex-wrap items-center gap-4 rounded-md border border-ei-mist bg-white p-5" data-testid={`cart-line-${line.id}`}>
                    <span className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-sm border border-ei-mist bg-ei-white">
                      {line.image ? <img src={line.image} alt="" className="h-full w-full object-contain" data-testid={`cart-image-${line.id}`} /> : null}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-ei-navy">{row?.name || line.name}</p>
                      <p className="mt-1 text-xs text-ei-graphite/65">{!row ? 'Pricing…' : row.unit_price_ex_gst === null ? 'Quote required' : `${money(row.unit_price_ex_gst)} ex GST each`}</p>
                      {Boolean(line.includes?.length) && <ul className="mt-2 space-y-0.5 text-xs text-ei-graphite/60" data-testid={`cart-includes-${line.id}`}>{line.includes.map((item) => <li key={item}>· {item}</li>)}</ul>}
                      {row && !row.card_payable && <p className="mt-2 text-xs text-amber-900" data-testid={`cart-line-blocked-${line.id}`}>{row.reason}</p>}
                    </div>
                    <input type="number" min="1" max="50" value={line.quantity} onChange={(e) => cart.setQuantity(line.id, line.kind, e.target.value)}
                      className="h-11 w-20 rounded-sm border border-ei-mist px-3 text-sm tnum" aria-label={`Quantity for ${line.name}`} data-testid={`cart-qty-${line.id}`} />
                    <span className="w-28 text-right font-semibold text-ei-navy tnum" data-testid={`cart-line-total-${line.id}`}>{row && row.line_total_ex_gst !== null ? money(row.line_total_ex_gst) : '—'}</span>
                    <button type="button" onClick={() => cart.remove(line.id, line.kind)} aria-label={`Remove ${line.name}`} className="h-11 w-11 inline-flex items-center justify-center text-ei-graphite/50 hover:text-red-700" data-testid={`cart-remove-${line.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                );
              })}
            </div>

            <aside className="mx-auto w-full max-w-2xl rounded-md bg-ei-navy p-8 sm:p-10 text-ei-white" data-testid="cart-summary">
              <h2 className="font-display text-xl font-bold sm:text-2xl">Order summary</h2>
              <dl className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between"><dt>Equipment (ex GST)</dt><dd className="tnum" data-testid="cart-subtotal">{priced ? money(priced.subtotal_ex_gst) : '—'}</dd></div>
                <div className="flex justify-between"><dt>GST {priced?.gst_rate_percent ?? 10}%</dt><dd className="tnum" data-testid="cart-gst">{priced ? money(priced.gst) : '—'}</dd></div>
                <div className="flex justify-between border-t border-white/15 pt-3 text-base font-bold"><dt>Total</dt><dd className="tnum" data-testid="cart-total">{priced ? money(priced.total_inc_gst) : '—'}</dd></div>
              </dl>
              <p className="mt-4 text-xs text-ei-white/60">{priced?.freight_note || 'Equipment and GST shown here. Estimated freight including GST is added to your card payment at checkout.'}</p>

              <div className="mt-5 border-t border-white/15 pt-5" data-testid="cart-freight-panel">
                <h3 className="font-display text-sm font-bold">Delivery or pickup</h3>
                <div className="mt-3 inline-flex rounded-sm border border-white/25 p-0.5" role="group" aria-label="Freight method">
                  {[['delivery', 'Delivery'], ['pickup', 'Pickup']].map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => { setFreightMode(id); setFreight(null); setFreightError(''); }}
                      aria-pressed={freightMode === id}
                      className={`min-h-[40px] rounded-sm px-4 text-sm font-bold transition-colors ${freightMode === id ? 'bg-ei-gold text-ei-navy' : 'text-ei-white/80 hover:bg-white/10'}`}
                      data-testid={`cart-freight-mode-${id}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <input
                    inputMode="numeric"
                    maxLength={4}
                    value={postcode}
                    onChange={(e) => lookupSuburbs(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="Postcode"
                    aria-label="Delivery postcode"
                    className="h-11 w-28 rounded-sm border border-white/25 bg-white/10 px-3 text-sm text-ei-white placeholder:text-ei-white/40 tnum"
                    data-testid="cart-freight-postcode"
                  />
                  <select
                    value={suburb}
                    onChange={(e) => { setSuburb(e.target.value); setFreight(null); }}
                    disabled={!suburbs.length}
                    aria-label="Delivery suburb"
                    className="h-11 flex-1 min-w-0 rounded-sm border border-white/25 bg-white/10 px-2 text-sm text-ei-white disabled:opacity-50"
                    data-testid="cart-freight-suburb"
                  >
                    <option value="">{suburbs.length ? 'Select suburb' : 'Enter postcode'}</option>
                    {suburbs.map((item) => <option key={item.suburb} value={item.suburb} className="text-ei-navy">{item.suburb}</option>)}
                  </select>
                </div>
                {freightMode === 'delivery' && (
                  <label className="mt-3 flex items-center gap-2 text-xs text-ei-white/80" data-testid="cart-freight-tailgate-label">
                    <input type="checkbox" className="h-4 w-4" checked={tailgate} onChange={(e) => { setTailgate(e.target.checked); setFreight(null); }} data-testid="cart-freight-tailgate" />
                    Tailgate delivery needed (no forklift on site)
                  </label>
                )}
                <button
                  type="button"
                  onClick={estimateFreight}
                  disabled={!suburb || freightBusy}                  className="mt-3 inline-flex w-full min-h-[44px] items-center justify-center gap-2 rounded-sm border border-white/30 px-4 py-3 text-sm font-bold text-ei-white hover:bg-white/10 disabled:opacity-50"
                  data-testid="cart-freight-estimate"
                >
                  {freightBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}{freightMode === 'pickup' ? 'Find my pickup warehouse' : 'Estimate freight'}
                </button>
                {freight?.freight_estimate_inc_gst != null && (
                  <dl className="mt-4 space-y-2 text-sm" data-testid="cart-freight-result">
                    <div className="flex justify-between"><dt>{freight.mode === 'pickup' ? 'Freight (pickup)' : 'Estimated freight (inc GST)'}</dt><dd className="tnum" data-testid="cart-freight-amount">{money(freight.freight_estimate_inc_gst)}</dd></div>
                    <div className="flex justify-between font-bold"><dt>{freight.mode === 'pickup' ? 'Total' : 'Estimated landed total'}</dt><dd className="tnum" data-testid="cart-landed-total">{money((priced?.total_inc_gst || 0) + freight.freight_estimate_inc_gst)}</dd></div>
                    <p className="pt-1 text-xs text-ei-white/60" data-testid="cart-freight-warehouse">
                      {freight.mode === 'pickup'
                        ? `Closest warehouse: ${freight.dispatch?.name} — ${freight.dispatch?.suburb} ${freight.dispatch?.postcode}. `
                        : `Dispatch from ${freight.dispatch?.name} (${freight.dispatch?.suburb} ${freight.dispatch?.postcode}). `}
                      {freight.note}
                    </p>                  </dl>
                )}
                {freightError && <p className="mt-3 text-xs text-amber-100" data-testid="cart-freight-error">{freightError}</p>}
              </div>
              {priced?.mode === 'test' && <p className="mt-3 rounded-sm border border-amber-300/40 bg-amber-100/10 p-3 text-xs text-amber-100" data-testid="cart-test-mode">Preview runs on Stripe test keys. Never enter a real card here.</p>}
              {params.get('cancelled') && <p className="mt-3 text-xs text-ei-white/70" data-testid="cart-cancelled">Checkout was cancelled. Nothing was charged.</p>}
              {error && <p className="mt-3 text-xs text-red-200" data-testid="cart-error">{error}</p>}
              <button type="button" onClick={checkout} disabled={busy || !priced || priced.lines.some((line) => !line.card_payable)}
                className="mt-6 inline-flex w-full min-h-[44px] items-center justify-center gap-2 rounded-sm bg-ei-gold px-6 py-4 font-display text-sm font-bold text-ei-navy hover:bg-[#b89747] disabled:opacity-50"
                data-testid="cart-checkout">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}{customer ? 'Pay now by card' : 'Sign in to pay by card'}
              </button>
              <Link to="/request-a-quote?cart=1" className="mt-3 inline-flex w-full min-h-[44px] items-center justify-center rounded-sm border border-white/30 px-6 py-3 text-sm font-bold text-ei-white hover:bg-white/10" data-testid="cart-quote-instead">Request a trade quote for this cart</Link>
            </aside>
          </div>
        )}
      </section>
    </>
  );
}
